import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Loading, GeneralStatus, PrimaryAltButton } from '../atoms';
import {
  DisplayTable,
  DisplayTableHead,
  DisplayTableRow,
  NewCourseModal
} from '../molecules';
import { Request, APIEndPoints } from '../../apis';
import { z } from 'zod';
import type { TCourse } from '../../common/types/Course';
import { handleOpen, handleClose } from '../../common';
import { useLoggedInUserStore } from '../../contexts/LoggedInUserContext';
import { useParams } from 'react-router-dom';

type TCourses = [TCourse] | null | [];

const ButtonContainer = styled.div`
  margin-bottom: 2.8125rem;
`;

const AccountCourses = () => {
  const [coursesDetails, setCoursesDetails] = useState<TCourses>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [updateSuccessful, setUpdateSuccessful] = useState(false);
  const [triggerUpdate, setTriggerUpdate] = useState(false);

  const { merchantId } = useParams();
  // TODO log
  // console.log('AccountCourse: merchantId', merchantId);
  useEffect(() => {
    setCoursesDetails(null);
    const getCourses = async () => {
      //#TODO rework with courses per merchant --------------------------
      // const merchantID = 'EP00001';

      const courses = await Request.safeGet({
        path: `${APIEndPoints.courses}/${merchantId}?access_token`,
        schema: z.any()
      });
      if (courses.type === 'right') {
        setCoursesDetails(courses.value);
      } else {
        setCoursesDetails([]);
      }
    };
    getCourses();
  }, [merchantId, triggerUpdate, modalOpen]);

  const refreshPage = () => {
    if (updateSuccessful) {
      setTriggerUpdate(!triggerUpdate);
    }
    handleClose(setModalOpen);
  };

  return (
    <>
      <ButtonContainer>
        <PrimaryAltButton
          type="button"
          onClick={() => handleOpen(setModalOpen)}
        >
          Add course <span className="material-symbols-rounded">add</span>
        </PrimaryAltButton>
        <NewCourseModal
          isModalOpen={modalOpen}
          handleModalClose={() => refreshPage()}
          setUpdateSuccessful={setUpdateSuccessful}
        />
      </ButtonContainer>

      {coursesDetails === null && <Loading />}
      {coursesDetails !== null && coursesDetails.length === 0 && (
        <DisplayTable colWidths={['auto']} colWidthsMd={['auto']}>
          <DisplayTableRow
            type="div"
            cells={[
              {
                contentType: 'error',
                content: 'No Courses found'
              }
            ]}
          />
        </DisplayTable>
      )}
      {coursesDetails !== null && coursesDetails.length > 0 && (
        <>
          <DisplayTable
            colWidths={['169px', 'auto', '150px', '230px', '150px', '138px']}
            colWidthsMd={[
              '9.375rem',
              'auto',
              '7.5rem',
              '10rem',
              '7rem',
              '6rem'
            ]}
          >
            <DisplayTableHead
              cols={[
                'Course Code',
                'Course Name',
                'Product',
                'Course Amount',
                'Status',
                'Created Date'
              ]}
            />
            {coursesDetails.map(
              ({
                id,
                polCode,
                polName,
                productType,
                merchantId,
                cost,
                status,
                createdAt
              }) => (
                <DisplayTableRow
                  key={id}
                  type="link"
                  action={`/account/${merchantId}/course/${id}`}
                  cells={[
                    {
                      contentType: 'text',
                      content: polCode
                    },
                    {
                      contentType: 'text',
                      content: polName
                    },
                    {
                      contentType: 'text',
                      content: productType
                    },
                    {
                      contentType: 'currency',
                      content: cost
                    },
                    {
                      contentType: 'text',
                      content: <GeneralStatus status={status} small />
                    },
                    {
                      contentType: 'date',
                      content: createdAt
                    }
                  ]}
                />
              )
            )}
          </DisplayTable>
        </>
      )}
    </>
  );
};

export { AccountCourses };
